import React from "react";
import classes from "../memory.module.css";
import CustomModal from "../../../components/Modals/CustomModal";
import TypographyInline from "../../../components/Typography/TypographyInline";
import {
  BASE,
  BORDER,
  GREY,
  PRIMARY,
  SUCCESS,
} from "../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../utils/constants/UI";
import Button from "../../../components/Buttons/Button";
import InputField from "../../../components/InputFields/InputField";
import TextArea from "../../../components/InputFields/TextArea";
import HtmlEditor from "../../../components/Editor/HtmlEditor";
import IconComponent from "../../../components/Icon/IconComponent";

const MemorySourceModal = ({
  open,
  selectedSource,
  sourceOptions,
  hasExistingSummary,
  websiteUrl,
  onWebsiteChange,
  selectedFile,
  onFileChange,
  manualFields,
  onManualFieldChange,
  manualContent,
  onManualContentChange,
  onClose,
  onSelectSource,
  onGenerate,
  isGenerating,
}) => {
  const currentSource = sourceOptions.find(
    (item) => item.id === selectedSource,
  );

  if (!currentSource) {
    return null;
  }

  const isGenerateDisabled =
    isGenerating ||
    (currentSource.id === "website" && !websiteUrl.trim()) ||
    (currentSource.id === "pdf" && !selectedFile) ||
    (currentSource.id === "manual" &&
      !manualContent &&
      !manualFields.website &&
      !manualFields.companyName &&
      !manualFields.businessDescription &&
      !manualFields.awards);

  return (
    <CustomModal
      width={currentSource.id === "manual" ? "56vw" : "46vw"}
      blurBackdrop={true}
      isCentered={selectedSource === "pdf"}
      open={open}
      onClose={onClose}
      header={{
        heading: {
          label: hasExistingSummary
            ? `Add Knowledge from ${currentSource.label}`
            : currentSource.modalTitle,
        },
      }}
      footer={{
        right: (
          <div className={classes.actionButtons}>
            <Button
              label="Cancel"
              variant="ghost"
              size="sm36"
              onClick={onClose}
            />
            <Button
              label={isGenerating ? "Starting..." : currentSource.cta}
              variant="primary"
              size="sm36"
              disabled={isGenerateDisabled}
              onClick={() => onGenerate(currentSource.id)}
            />
          </div>
        ),
      }}
    >
      <div className={classes.sourceModalBody}>
        <div className={classes.sourceModalIntroCompact}>
          <TypographyInline
            label={
              hasExistingSummary
                ? "Add one more source to improve or expand the current knowledge base."
                : "Configure the selected source and start the generation job."
            }
            size="md"
            fontWeight={fontWeight.SEMIBOLD}
            color={BASE.BLACK}
          />
          <TypographyInline
            label={currentSource.qualityHint}
            size="sm"
            fontWeight={fontWeight.REGULAR}
            color={GREY.SECONDARY}
          />
        </div>

        <div
          className={`${classes.sourceFormPanel} ${classes.sourceFormPanelVisible}`}
        >
          <div className={classes.sourceFormHeader}>
            <div className={classes.sourceModalHeaderRow}>
              <div className={classes.sourceChoiceIconWrap}>
                <img
                  src={currentSource.asset}
                  alt={currentSource.label}
                  className={classes.sourceChoiceIcon}
                />
              </div>
              <div>
                <TypographyInline
                  label={currentSource.label}
                  size="lg"
                  fontWeight={fontWeight.BOLD}
                  color={BASE.BLACK}
                />
                <TypographyInline
                  label={currentSource.description}
                  size="sm"
                  fontWeight={fontWeight.REGULAR}
                  color={GREY.SECONDARY}
                />
              </div>
            </div>
            <button
              type="button"
              className={classes.switchSourceLink}
              onClick={() => onClose()}
            >
              <TypographyInline
                label="Change source"
                size="sm"
                fontWeight={fontWeight.SEMIBOLD}
                color={PRIMARY.W_500}
              />
            </button>
          </div>

          {currentSource.id === "website" && (
            <div className={classes.sourceInputStack}>
              <TypographyInline
                label="Website URL"
                size="sm"
                fontWeight={fontWeight.SEMIBOLD}
                color={BASE.BLACK}
                isRequired
              />
              <InputField
                placeholder="Enter website URL here..."
                value={websiteUrl}
                onChange={(event) => onWebsiteChange(event.target.value)}
                size="md40"
              />
            </div>
          )}

          {currentSource.id === "pdf" && (
            <button
              type="button"
              className={`${classes.fileUploadContainer} ${classes.fileUploadContainerCompact}`}
              onClick={() =>
                document.getElementById("memory-pdf-upload-input")?.click()
              }
            >
              <input
                id="memory-pdf-upload-input"
                type="file"
                accept=".pdf"
                style={{ display: "none" }}
                onChange={(event) =>
                  onFileChange(event.target.files?.[0] || null)
                }
              />
              <IconComponent
                iconLabel={
                  selectedFile
                    ? ICON_LABELS.CHECK_CIRCLE
                    : ICON_LABELS.UPLOAD_FILE
                }
                fontSize="56px"
                color={selectedFile ? SUCCESS.W_500 : PRIMARY.W_500}
              />
              <div className={classes.modalContentWrapper}>
                <TypographyInline
                  label={selectedFile ? selectedFile.name : "Select a PDF"}
                  size="lg"
                  fontWeight={fontWeight.BOLD}
                  color={selectedFile ? SUCCESS.W_500 : BASE.BLACK}
                />
                <TypographyInline
                  label="PDF files only. Structured documents typically lead to the strongest summaries."
                  size="sm"
                  fontWeight={fontWeight.REGULAR}
                  color={GREY.SECONDARY}
                />
              </div>
            </button>
          )}

          {currentSource.id === "manual" && (
            <div className={classes.manualComposerCompact}>
              <div className={classes.manualFieldGrid}>
                <div className={classes.sourceInputStack}>
                  <TypographyInline
                    label="Website"
                    size="sm"
                    fontWeight={fontWeight.SEMIBOLD}
                    color={BASE.BLACK}
                  />
                  <InputField
                    placeholder="Enter website URL"
                    value={manualFields.website}
                    onChange={(event) =>
                      onManualFieldChange((previous) => ({
                        ...previous,
                        website: event.target.value,
                      }))
                    }
                    size="md40"
                  />
                </div>
                <div className={classes.sourceInputStack}>
                  <TypographyInline
                    label="Company Name"
                    size="sm"
                    fontWeight={fontWeight.SEMIBOLD}
                    color={BASE.BLACK}
                  />
                  <InputField
                    placeholder="Enter company name"
                    value={manualFields.companyName}
                    onChange={(event) =>
                      onManualFieldChange((previous) => ({
                        ...previous,
                        companyName: event.target.value,
                      }))
                    }
                    size="md40"
                  />
                </div>
                <div className={classes.sourceInputStack}>
                  <TypographyInline
                    label="Business Description"
                    size="sm"
                    fontWeight={fontWeight.SEMIBOLD}
                    color={BASE.BLACK}
                  />
                  <TextArea
                    isResize={true}
                    placeholder="Describe your business..."
                    value={manualFields.businessDescription}
                    onChange={(event) =>
                      onManualFieldChange((previous) => ({
                        ...previous,
                        businessDescription: event.target.value,
                      }))
                    }
                    rows={4}
                    style={{
                      width: "100%",
                      padding: "10px",
                      borderRadius: "8px",
                      borderColor: BORDER.LIGHT,
                    }}
                  />
                </div>
                <div className={classes.sourceInputStack}>
                  <TypographyInline
                    label="Awards"
                    size="sm"
                    fontWeight={fontWeight.SEMIBOLD}
                    color={BASE.BLACK}
                  />
                  <InputField
                    placeholder="List notable awards or certifications"
                    value={manualFields.awards}
                    onChange={(event) =>
                      onManualFieldChange((previous) => ({
                        ...previous,
                        awards: event.target.value,
                      }))
                    }
                    size="md40"
                  />
                </div>
              </div>
              <div className={classes.sourceInputStack}>
                <TypographyInline
                  label="Additional Information"
                  size="sm"
                  fontWeight={fontWeight.SEMIBOLD}
                  color={BASE.BLACK}
                />
                <div className={classes.editorFrame}>
                  <HtmlEditor
                    id="memory-manual-editor"
                    editorHeight="340px"
                    onInput={onManualContentChange}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default MemorySourceModal;
